export interface GcpParseResult {
  pointCount: number;
  minStart: Date | null;
  maxEnd: Date | null;
  minStartLocalDate?: string | null;
  maxStartLocalDate?: string | null;
  error?: string;
}

// Estimated time for GCP equipment setup and preparation, added once per upload session by the caller.
export const PREP_MS = 30 * 60 * 1000;

interface ParsedTimestamp {
  instant: Date;
  localDate: string;
}

function parseTimestamp(str: string): ParsedTimestamp | null {
  const s = str.trim();
  if (!s) return null;
  const m = s.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2}(?:\.\d+)?)\s+UTC([+-]\d{2}:\d{2})$/);
  if (!m) return null;
  // Normalize fractional seconds to exactly 3 digits — Safari rejects non-standard lengths
  const timePart = m[2].replace(/\.(\d+)$/, (_, f) => '.' + f.padEnd(3, '0').slice(0, 3));
  const instant = new Date(`${m[1]}T${timePart}${m[3]}`);
  return isNaN(instant.getTime()) ? null : { instant, localDate: m[1] };
}

const PARSEABLE_EXTENSIONS = ['.csv', '.tsv'];

export function isGcpParseableFile(filename: string): boolean {
  const lower = filename.toLowerCase();
  return PARSEABLE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

// Quote-aware CSV splitter; TSV lines don't use RFC 4180 quoting so we split directly on tab
function splitLine(line: string, delimiter: string): string[] {
  if (delimiter === '\t') return line.split('\t');
  const cols: string[] = [];
  let current = '';
  let inQuotes = false;
  for (const ch of line) {
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      cols.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  cols.push(current);
  return cols;
}

export function parseGcpCsvText(text: string): GcpParseResult {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { pointCount: 0, minStart: null, maxEnd: null, error: 'File appears to be empty' };
  }

  // Detect delimiter: Emlid TSV exports use tabs, CSV exports use commas
  const delimiter = lines[0].includes('\t') ? '\t' : ',';
  const headers = splitLine(lines[0], delimiter).map((h) => h.trim());
  const startIdx = headers.indexOf('Averaging start');
  const endIdx = headers.indexOf('Averaging end');

  if (startIdx === -1 || endIdx === -1) {
    return {
      pointCount: 0,
      minStart: null,
      maxEnd: null,
      error: 'The file does not contain time marks. Please upload the initial file from Emlid',
    };
  }

  let pointCount = 0;
  let minStart: Date | null = null;
  let maxEnd: Date | null = null;
  let minStartLocalDate: string | null = null;
  let maxStartLocalDate: string | null = null;
  const maxIdx = Math.max(startIdx, endIdx);

  for (let i = 1; i < lines.length; i++) {
    const cols = splitLine(lines[i], delimiter);
    if (cols.length <= maxIdx) continue;

    const start = parseTimestamp(cols[startIdx]);
    const end = parseTimestamp(cols[endIdx]);

    if (start && end) {
      pointCount++;
      if (!minStart || start.instant < minStart) {
        minStart = start.instant;
        minStartLocalDate = start.localDate;
      }
      if (!maxStartLocalDate || start.localDate > maxStartLocalDate) maxStartLocalDate = start.localDate;
      if (!maxEnd || end.instant > maxEnd) maxEnd = end.instant;
    }
  }

  if (pointCount === 0) {
    return {
      pointCount: 0,
      minStart: null,
      maxEnd: null,
      error: 'The file does not contain time marks. Please upload the initial file from Emlid.',
    };
  }

  return { pointCount, minStart, maxEnd, minStartLocalDate, maxStartLocalDate };
}

export async function parseGcpCsvFile(file: File): Promise<GcpParseResult> {
  return parseGcpCsvText(await file.text());
}

const ALLOWED_URL_PROTOCOLS = ['http:', 'https:'];
const ALLOWED_URL_DOMAINS = ['airtableusercontent.com'];

const MAX_CSV_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB — sanity cap, GCP CSVs are tiny
const FETCH_TIMEOUT_MS = 15_000;

function isAllowedHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return ALLOWED_URL_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

function isFetchableHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ALLOWED_URL_PROTOCOLS.includes(parsed.protocol) && isAllowedHostname(parsed.hostname);
  } catch {
    return false;
  }
}

// Fetches an already-uploaded measurement file (e.g. restored from a saved GCP request) and
// parses it the same way a freshly-selected file would be. Returns null on any network/HTTP
// failure, on a malformed / non-http(s) / non-allowlisted url, on an oversized response, or
// on timeout, so callers can silently omit it from counts instead of crashing.
export async function parseGcpCsvUrl(url: string): Promise<GcpParseResult | null> {
  if (!isFetchableHttpUrl(url)) return null;

  try {
    const res = await fetch(url, {
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      credentials: 'omit', // never leak our cookies/auth to the file host
      redirect: 'error',
    });
    if (!res.ok) return null;

    // Reject obviously oversized payloads before reading the body.
    // Content-Length may be absent (chunked), so also cap after reading.
    const contentLength = res.headers.get('content-length');
    if (contentLength && Number(contentLength) > MAX_CSV_SIZE_BYTES) return null;

    const text = await res.text();
    if (text.length > MAX_CSV_SIZE_BYTES) return null;

    return parseGcpCsvText(text);
  } catch {
    return null;
  }
}

/** Each file's first and last measurement day, sorted and deduplicated — not every day in between. */
export function getGcpMeasurementDates(results: Array<GcpParseResult | null | undefined>): string[] {
  const dates = new Set<string>();
  for (const r of results) {
    if (r?.minStartLocalDate) dates.add(r.minStartLocalDate);
    if (r?.maxStartLocalDate) dates.add(r.maxStartLocalDate);
  }
  return [...dates].sort();
}

export function calcGcpHoursFromResults(results: Array<GcpParseResult | null | undefined>): number | undefined {
  const valid = results.filter((r): r is GcpParseResult => !!r && !r.error && r.minStart !== null && r.maxEnd !== null);
  if (valid.length === 0) return undefined;

  // Sum per-file durations so two separate sessions don't inflate hours to the full
  // calendar gap between them. Prep time and rounding are applied once by the caller.
  const totalMs = valid.reduce((sum, r) => sum + (r.maxEnd!.getTime() - r.minStart!.getTime()), 0);
  return totalMs / (1000 * 60 * 60);
}

export function calcGcpCountFromResults(results: Array<GcpParseResult | null | undefined>): number | undefined {
  const valid = results.filter((r): r is GcpParseResult => !!r && !r.error);
  if (valid.length === 0) return undefined;
  const total = valid.reduce((s, r) => s + r.pointCount, 0);
  return total > 0 ? total : undefined;
}
